import { create } from 'zustand';

export type AgentStatus = 'stopped' | 'starting' | 'active' | 'paused' | 'error';

export interface AgentState {
  status: AgentStatus;
  strategyName: string;
  lastAction: string | null;
  lastActionAt: string | null;
  dailyPnl: number;
  tradesToday: number;
  errorMessage: string | null;
  startedAt: string | null;
}

export interface AgentDecision {
  id: string;
  decisionType: 'buy' | 'sell' | 'hold' | 'skip';
  symbol: string;
  quantity: number | null;
  price: number | null;
  notional: number | null;
  reasoning: string;
  confidence: number;
  outcome: 'pending' | 'executed' | 'rejected' | 'failed' | 'overridden' | 'awaiting_approval' | 'queued';
  createdAt: string;
}

interface AgentStore {
  state: AgentState | null;
  decisions: AgentDecision[];
  pendingApprovals: AgentDecision[];
  isLoading: boolean;
  error: string | null;
  setAgentState: (state: AgentState) => void;
  setDecisions: (decisions: AgentDecision[]) => void;
  addDecision: (decision: AgentDecision) => void;
  addPendingApproval: (decision: AgentDecision) => void;
  removePendingApproval: (decisionId: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useAgentStore = create<AgentStore>((set) => ({
  state: null,
  decisions: [],
  pendingApprovals: [],
  isLoading: false,
  error: null,
  setAgentState: (state) => set({ state }),
  setDecisions: (decisions) => set({ decisions }),
  addDecision: (decision) => set((s) => ({ decisions: [decision, ...s.decisions].slice(0, 100) })),
  addPendingApproval: (decision) =>
    set((s) => ({
      pendingApprovals: [...s.pendingApprovals.filter((d) => d.id !== decision.id), decision],
    })),
  removePendingApproval: (decisionId) =>
    set((s) => ({
      pendingApprovals: s.pendingApprovals.filter((d) => d.id !== decisionId),
    })),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
}));

export default useAgentStore;
